import { InsightsService } from '../insights-service/insights-service';
import { WebVitalsTracker } from './web-vitals-tracker';
import { ErrorTracker } from './error-tracker';
import { ReportingTracker } from './reporting-tracker';
import { UserContextTracker } from './user-context-tracker';
import { TelemetryEventType } from './telemetry-models';
import type { CustomMetric, FeatureFlags, TelemetryConfig, TelemetryEvent, TelemetryPayload } from './telemetry-models';

const FLUSH_INTERVAL_MS = 5000;
const DEFAULT_BUFFER_SIZE = 100;

/**
 * Coordinates telemetry tracking across Web Vitals, errors, and custom metrics.
 * Buffers events and flushes to backend service periodically.
 */
export class TelemetryService {
  private static _instance: TelemetryService;
  private _isInitialized = false;
  private _config: TelemetryConfig | null = null;
  private _insightsService: InsightsService | null = null;
  private _features: FeatureFlags;
  private _sessionId: string;
  private _navigationType: 'navigate' | 'reload' | 'back_forward' | 'prerender';
  private _eventBuffer: TelemetryEvent[] = [];
  private _flushTimer: ReturnType<typeof setInterval> | null = null;
  private _webVitalsTracker: WebVitalsTracker | null = null;
  private _errorTracker: ErrorTracker | null = null;
  private _reportingTracker: ReportingTracker | null = null;
  private _userContextTracker: UserContextTracker | null = null;

  private constructor() {
    this._features = this._detectFeatures();
    this._navigationType = this._getNavigationType();
    this._sessionId = this._generateSessionId();
  }

  static getInstance(): TelemetryService {
    if (!TelemetryService._instance) {
      TelemetryService._instance = new TelemetryService();
    }
    return TelemetryService._instance;
  }

  /** Initialize telemetry with tracking endpoint and configuration. */
  static init(config: TelemetryConfig): void {
    const instance = TelemetryService.getInstance();
    if (instance._isInitialized) {
      instance._log('WARN: TelemetryService already initialized');
      return;
    }

    performance.mark('telemetry:init:start');

    try {
      instance._config = {
        maxBufferSize: DEFAULT_BUFFER_SIZE,
        enabledLogging: false,
        enableWebVitals: false,
        enableErrorTracking: false,
        enableReporting: false,
        enableUserContext: false,
        ...config,
      };

      instance._insightsService = InsightsService.createInstance();
      instance._insightsService.init({
        trackingServiceUrl: config.trackingServiceUrl,
        enabledLogging: config.enabledLogging,
      });

      instance._isInitialized = true;
      instance.setup();

      performance.mark('telemetry:init:end');
      performance.measure('telemetry:init', 'telemetry:init:start', 'telemetry:init:end');

      const measure = performance.getEntriesByName('telemetry:init')[0] as PerformanceMeasure;
      if (measure && measure.duration > 50) {
        instance._log(`WARN: Init took ${measure.duration.toFixed(2)}ms (target <50ms)`);
      }
    } catch (error) {
      // Never break the host app
      instance._log('ERROR: Failed to initialize TelemetryService', error);
    }
  }

  /** Start all trackers and monitoring. */
  public async setup(): Promise<void> {
    if (!this._isInitialized || !this._config) {
      this._log('ERROR: Cannot setup, service not initialized');
      return;
    }

    try {
      // Start UserContextTracker if enabled
      if (this._config.enableUserContext) {
        this._userContextTracker = new UserContextTracker(
          (event) => this._bufferEvent(event),
          this._sessionId,
          this._config.enabledLogging,
          this._config.customContextData
        );
        await this._userContextTracker.start();
        this._log('UserContextTracker started');
      }

      // Start WebVitalsTracker if enabled
      if (this._config.enableWebVitals) {
        this._webVitalsTracker = new WebVitalsTracker(
          this._features,
          (event) => this._bufferEvent(event),
          this._config.enabledLogging
        );
        this._webVitalsTracker.start();
        this._log('WebVitalsTracker started');
      }

      // Start ErrorTracker if enabled
      if (this._config.enableErrorTracking) {
        this._errorTracker = new ErrorTracker(
          (event) => this._bufferEvent(event),
          this._navigationType,
          this._config.appOriginPatterns,
          this._config.enabledLogging
        );
        this._errorTracker.start();
        this._log('ErrorTracker started');
      }

      // Start ReportingTracker if enabled
      if (this._config.enableReporting) {
        this._reportingTracker = new ReportingTracker((event) => this._bufferEvent(event), this._config.enabledLogging);
        this._reportingTracker.start();
        this._log('ReportingTracker started');
      }

      this._startFlushTimer();
      this._setupVisibilityHandlers();
      this._log('TelemetryService started');
    } catch (error) {
      this._log('ERROR: Failed to setup TelemetryService', error);
    }
  }

  /** Stop all trackers and flush remaining events. */
  public teardown(): void {
    try {
      if (this._webVitalsTracker) {
        this._webVitalsTracker.stop();
        this._webVitalsTracker = null;
      }

      if (this._errorTracker) {
        this._errorTracker.stop();
        this._errorTracker = null;
      }

      if (this._reportingTracker) {
        this._reportingTracker.stop();
        this._reportingTracker = null;
      }

      if (this._userContextTracker) {
        this._userContextTracker.stop();
        this._userContextTracker = null;
      }

      if (this._flushTimer) {
        clearInterval(this._flushTimer);
        this._flushTimer = null;
      }

      document.removeEventListener('visibilitychange', this._handleVisibilityChange);
      window.removeEventListener('pagehide', this._handlePageHide);
      window.removeEventListener('pageshow', this._handlePageShow);

      this.flush();

      this._isInitialized = false;
      this._config = null;
      this._log('TelemetryService stopped');
    } catch (error) {
      this._log('ERROR: Failed to teardown TelemetryService', error);
    }
  }

  public trackMetric(name: string, value: number): void {
    if (typeof name !== 'string' || typeof value !== 'number') return;

    const metric: CustomMetric = {
      name,
      value,
      timestamp: Date.now(),
    };

    this._bufferEvent({ type: TelemetryEventType.CUSTOM, data: metric });
  }

  /** Create performance mark for debugging in DevTools. */
  public mark(name: string, detail?: unknown): void {
    if (!this._features.hasPerformanceMark) return;

    try {
      if (detail !== undefined) {
        performance.mark(name, { detail });
      } else {
        performance.mark(name);
      }
    } catch (error) {
      // Ignore errors
    }
  }

  public flush(): void {
    const events = this._eventBuffer.splice(0);
    if (events.length === 0) return;

    const payload: TelemetryPayload = {
      events,
      sessionId: this._sessionId,
      timestamp: Date.now(),
    };

    this._sendPayload(payload);
  }

  private _detectFeatures(): FeatureFlags {
    return {
      hasPerformanceObserver: typeof PerformanceObserver !== 'undefined',
      hasSessionStorage: this._checkSessionStorage(),
      hasIndexedDB: typeof indexedDB !== 'undefined',
      hasPerformanceMark: typeof performance !== 'undefined' && typeof performance.mark === 'function',
      hasVisibilityAPI: typeof document.visibilityState !== 'undefined',
    };
  }

  private _checkSessionStorage(): boolean {
    try {
      const test = '__test__';
      sessionStorage.setItem(test, test);
      sessionStorage.removeItem(test);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Generate or retrieve session ID that persists across page reloads.
   * Required to link all events within the same browsing session.
   */
  private _generateSessionId(): string {
    const SESSION_STORAGE_KEY = '__telemetry_session_id__';

    // Reuse existing session ID if available
    if (this._features.hasSessionStorage) {
      try {
        const existingId = sessionStorage.getItem(SESSION_STORAGE_KEY);
        if (existingId) {
          this._log(`Reusing existing session ID: ${existingId}`);
          return existingId;
        }
      } catch (error) {
        this._log('WARN: Failed to read session ID from sessionStorage', error);
      }
    }

    let sessionId: string;
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      sessionId = crypto.randomUUID().replace(/-/g, '');
    } else {
      sessionId = Date.now().toString(36) + Math.random().toString(36).substring(2, 15);
    }

    // Persist for future page loads
    if (this._features.hasSessionStorage) {
      try {
        sessionStorage.setItem(SESSION_STORAGE_KEY, sessionId);
        this._log(`Created new session ID: ${sessionId}`);
      } catch (error) {
        this._log('WARN: Failed to store session ID in sessionStorage', error);
      }
    }

    return sessionId;
  }

  private _getNavigationType(): 'navigate' | 'reload' | 'back_forward' | 'prerender' {
    if (typeof performance === 'undefined' || !performance.getEntriesByType) {
      return 'navigate';
    }
    const navEntries = performance.getEntriesByType('navigation');
    if (navEntries.length > 0) {
      const nav = navEntries[0] as PerformanceNavigationTiming;
      return nav.type as 'navigate' | 'reload' | 'back_forward' | 'prerender';
    }
    return 'navigate';
  }

  private _bufferEvent(event: TelemetryEvent): void {
    const maxSize = this._config?.maxBufferSize ?? DEFAULT_BUFFER_SIZE;

    // Check if an event of the same type already exists in the buffer
    const hasDuplicateType = this._eventBuffer.some((bufferedEvent) => bufferedEvent.type === event.type);

    if (hasDuplicateType) {
      // Flush existing events before adding duplicate type
      this._log(`Flushing buffer due to duplicate event type: ${event.type}`);
      this.flush();
    }

    if (this._eventBuffer.length >= maxSize) {
      this._eventBuffer.shift();
    }

    this._eventBuffer.push(event);
  }

  private _startFlushTimer(): void {
    this._flushTimer = setInterval(() => {
      this.flush();
    }, FLUSH_INTERVAL_MS);
  }

  private _setupVisibilityHandlers(): void {
    if (this._features.hasVisibilityAPI) {
      document.addEventListener('visibilitychange', this._handleVisibilityChange);
    }
    window.addEventListener('pagehide', this._handlePageHide);
    window.addEventListener('pageshow', this._handlePageShow);
  }

  private _handleVisibilityChange = (): void => {
    if (document.visibilityState === 'hidden') {
      this.flush();
    }
  };

  private _handlePageHide = (): void => {
    this.flush();
  };

  private _handlePageShow = (event: PageTransitionEvent): void => {
    // Restart tracking after bfcache restoration to avoid stale metrics
    if (event.persisted) {
      this._log('Page restored from bfcache');
      if (this._webVitalsTracker) {
        this._webVitalsTracker.stop();
        this._webVitalsTracker.start();
      }
    }
  };

  private _sendPayload(payload: TelemetryPayload): void {
    if (!this._insightsService) {
      this._log('ERROR: InsightsService not available');
      return;
    }

    try {
      const insightsEvents = payload.events.map((event) => ({
        type: event.type,
        sessionId: this._sessionId,
        path: window.location.pathname,
        ...event.data,
      }));

      this._insightsService.telemetryEvent(insightsEvents);
      this._insightsService.sendEvents();

      this._log(`Sent ${payload.events.length} events`);
    } catch (error) {
      this._log('ERROR: Failed to send payload', error);
    }
  }

  private _log(...args: unknown[]): void {
    if (this._config?.enabledLogging) {
      // eslint-disable-next-line no-console
      console.log('[TelemetryService]', ...args);
    }
  }
}
